"use client";
import { useRef } from "react";
import dynamic from "next/dynamic";
import { MapStoreListOverlay } from "modules/common/components/map/MapStoreListOverlay";
import { useMapInteraction } from "modules/common/components/map/hooks/useMapInteraction";
import useWotcLGS from "modules/common/components/map/hooks/useWotcLGS";
import type { Map as LeafletMap, Marker } from "leaflet";
import { CityStatsResponse } from "modules/city/model/cityStats";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import DynamicSelectLGSDialog from "modules/lgs/components/stores/settings/DynamicSelectLGSDialog";
import { MapErrorBoundary } from "modules/common/components/map/MapErrorBoundary";

// Dynamic import of MapView (pure rendering component) — avoids the Map default
// export wrapper which redundantly calls useUserLocation + useWotcLGS, creating
// duplicate store queries and an unnecessary reverseGeocode server action.
const MapViewComponent = dynamic(
  () =>
    import("modules/common/components/map/Map").then((mod) => ({
      default: mod.MapView,
    })),
  { ssr: false }
);

interface Props {
  latitude: string;
  longitude: string;
  cityName: string;
  stats?: CityStatsResponse;
  showStoreOverlay?: boolean;
}

export function CityMapSection({
  latitude,
  longitude,
  cityName,
  stats,
  showStoreOverlay = true,
}: Props) {
  const { userId } = useAppUser();
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRefs = useRef<Map<number, Marker>>(new Map());

  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const {
    data: stores,
    isLoading: isStoresLoading,
    isError: isStoresError,
    isPlaceholderData,
  } = useWotcLGS([lng, lat], cityName);

  const { selectedStoreWotcId, selectStoreOnMap, clearSelection } =
    useMapInteraction({
      mapRef,
      markerRefs,
    });

  return (
    <MapErrorBoundary className="rounded-box h-full w-full">
      <div className="relative h-full w-full">
        <MapViewComponent
          center={[lat, lng]}
          enableSearchBar={false}
          mapRef={mapRef}
          markerRefs={markerRefs}
          selectedStoreWotcId={selectedStoreWotcId}
          onStoreClick={selectStoreOnMap}
          onDeselect={clearSelection}
          className="rounded-box h-full w-full"
          zoom={12}
          padding={[50, 50]}
          data={stores}
          isLoading={isStoresLoading}
          isError={isStoresError}
          isPlaceholderData={isPlaceholderData}
        />

        {showStoreOverlay && stores && stores.length > 0 && (
          <MapStoreListOverlay
            stores={stores}
            cityName={cityName}
            position="top-left"
            selectedStoreWotcId={selectedStoreWotcId}
            onStoreClick={selectStoreOnMap}
            cityUserCount={stats?.userCount}
            hideCityLink
            isAuthenticated={!!userId}
          />
        )}

        <DynamicSelectLGSDialog />
      </div>
    </MapErrorBoundary>
  );
}
